import {
	Button,
	CheckboxGroup,
	Checkbox,
	DialogTrigger,
	Popover,
	SearchField,
	Tag,
	TagGroup,
	Text,
	ToggleButton,
	ToggleButtonGroup,
} from "@backstage/ui";
import type {
	BundleInventoryItem,
	BundleInventoryType,
} from "@internal/backstage-plugin-avd-common";
import { makeStyles } from "@material-ui/core/styles";
import { ReactNode, useState } from "react";

import {
	descriptorFor,
	type BundleInventoryDescriptor,
	type StatusBucket,
} from "./bundleDescriptors";
import {
	activeFilterCount,
	clearPopoverFilters,
	ARCHIVED_FILTERS,
	DRIFT_FILTERS,
	FILTER_GROUPS,
	HEALTH_FILTERS,
	HELD_FILTERS,
	IAC_FILTERS,
	LIFECYCLE_FILTERS,
	matchesFilters,
	type FilterGroup,
	type InventoryFilters,
} from "./inventoryFilters";

type Option = { value: string; label: string };

/** The status options for the types in play: the selected types', or every present type's, by id. */
export function statusBuckets(
	items: readonly BundleInventoryItem[],
	filters: InventoryFilters,
	descriptors: readonly BundleInventoryDescriptor[],
): StatusBucket[] {
	const present = [...new Set(items.map((i) => i.bundleType))].filter(
		(t) => !filters.type || filters.type.includes(t),
	);
	const seen = new Map<string, StatusBucket>();
	for (const type of present) {
		const own = items.filter((i) => i.bundleType === type);
		for (const bucket of descriptorFor(descriptors, type).buckets(own)) {
			if (!seen.has(bucket.id)) seen.set(bucket.id, bucket);
		}
	}
	return [...seen.values()];
}

const GROUP_LABEL: Record<FilterGroup, string> = {
	health: "Health",
	type: "Type",
	status: "Status",
	drift: "Drift",
	lifecycle: "Lifecycle",
	held: "Held",
	iac: "IaC version",
	archived: "Archived",
};

const FIXED_LABEL: Partial<Record<FilterGroup, Record<string, string>>> = {
	health: { failed: "Failed", attention: "Attention", healthy: "Healthy" },
	drift: {
		drifted: "Drifted",
		"in-sync": "In sync",
		error: "Check failed",
		unchecked: "Never checked",
	},
	lifecycle: { active: "Active", expiring: "Expiring" },
	held: { held: "Held", "not-held": "Not held" },
	iac: { behind: "Behind main", current: "Up to date" },
	archived: { include: "Show archived" },
};

/** Every option of every group; type and status come from what the registry holds. */
function groupOptions(
	items: readonly BundleInventoryItem[],
	types: readonly BundleInventoryType[],
	filters: InventoryFilters,
	descriptors: readonly BundleInventoryDescriptor[],
): Record<FilterGroup, Option[]> {
	const fixed = (group: FilterGroup, values: readonly string[]): Option[] =>
		values.map((value) => ({ value, label: FIXED_LABEL[group]?.[value] ?? value }));
	return {
		health: fixed("health", HEALTH_FILTERS),
		type: types.map((t) => ({ value: t.bundleType, label: t.displayName })),
		status: statusBuckets(items, filters, descriptors).map((b) => ({
			value: b.id,
			label: b.label,
		})),
		drift: fixed("drift", DRIFT_FILTERS),
		lifecycle: fixed("lifecycle", LIFECYCLE_FILTERS),
		held: fixed("held", HELD_FILTERS),
		iac: fixed("iac", IAC_FILTERS),
		archived: fixed("archived", ARCHIVED_FILTERS),
	};
}

/** One group's values replaced; picking types drops statuses the chosen types do not offer. */
function withGroup(
	filters: InventoryFilters,
	group: FilterGroup,
	values: string[],
	items: readonly BundleInventoryItem[],
	descriptors: readonly BundleInventoryDescriptor[],
): InventoryFilters {
	const next = { ...filters, [group]: values.length ? values : undefined } as InventoryFilters;
	if (group === "type" && next.status) {
		const offered = new Set(statusBuckets(items, next, descriptors).map((b) => b.id));
		const kept = next.status.filter((s) => offered.has(s));
		next.status = kept.length ? kept : undefined;
	}
	return next;
}

const useStyles = makeStyles((theme) => ({
	controls: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1.5),
		flexWrap: "wrap",
	},
	count: {
		display: "inline-grid",
		placeItems: "center",
		minWidth: 18,
		height: 18,
		marginLeft: theme.spacing(0.75),
		borderRadius: "var(--bui-radius-full)",
		background: "var(--bui-bg-solid)",
		color: "var(--bui-fg-solid)",
		fontSize: "var(--bui-font-size-1)",
	},
	panel: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))",
		gap: theme.spacing(2, 3),
		width: 440,
		maxWidth: "calc(100vw - 48px)",
	},
	panelHead: { gridColumn: "1 / -1" },
	panelActions: {
		gridColumn: "1 / -1",
		display: "flex",
		alignItems: "center",
		justifyContent: "space-between",
		paddingTop: theme.spacing(1.5),
		borderTop: "1px solid var(--bui-border-1)",
	},
	optionCount: { marginLeft: theme.spacing(0.75), color: "var(--bui-fg-secondary)" },
	tags: {
		display: "flex",
		alignItems: "center",
		gap: theme.spacing(1),
		flexWrap: "wrap",
		marginBottom: theme.spacing(1),
	},
}));

type FilterProps = Readonly<{
	items: readonly BundleInventoryItem[];
	types: readonly BundleInventoryType[];
	filters: InventoryFilters;
	descriptors: readonly BundleInventoryDescriptor[];
	onChange: (filters: InventoryFilters) => void;
}>;

/** The Filters button and its popover: a checkbox group per filter, each option with its row count. */
function FiltersButton(props: FilterProps): ReactNode {
	const classes = useStyles();
	const { items, types, filters, descriptors, onChange } = props;
	const [open, setOpen] = useState(false);
	const options = groupOptions(items, types, filters, descriptors);
	const total = activeFilterCount(filters);
	const matching = items.filter((i) =>
		matchesFilters(i, filters, descriptorFor(descriptors, i.bundleType)),
	).length;
	// Each option counts the rows it would show: every other filter still applies.
	const count = (group: FilterGroup, value: string) =>
		items.filter((i) =>
			matchesFilters(
				i,
				{ ...filters, [group]: [value] },
				descriptorFor(descriptors, i.bundleType),
			),
		).length;

	return (
		<DialogTrigger isOpen={open} onOpenChange={setOpen}>
			<Button variant="secondary" size="small">
				Filters
				{total > 0 ? (
					<span className={classes.count} aria-label={`${total} active`}>
						{total}
					</span>
				) : null}
			</Button>
			<Popover placement="bottom end">
				<div className={classes.panel}>
					<Text as="h3" variant="title-small" className={classes.panelHead}>
						Filters
					</Text>
					{FILTER_GROUPS.filter((g) => options[g].length > 0).map((group) => (
						<CheckboxGroup
							key={group}
							label={GROUP_LABEL[group]}
							value={filters[group] ?? []}
							onChange={(values) =>
								onChange(withGroup(filters, group, values, items, descriptors))
							}
						>
							{options[group].map((o) => (
								<Checkbox key={o.value} value={o.value}>
									{o.label}{" "}
									<span className={classes.optionCount}>
										{count(group, o.value)}
									</span>
								</Checkbox>
							))}
						</CheckboxGroup>
					))}
					<div className={classes.panelActions}>
						<Button
							variant="tertiary"
							size="small"
							isDisabled={total === 0}
							onPress={() => onChange(clearPopoverFilters(filters))}
						>
							Clear all
						</Button>
						<Button variant="primary" size="small" onPress={() => setOpen(false)}>
							Show {matching} {matching === 1 ? "bundle" : "bundles"}
						</Button>
					</div>
				</div>
			</Popover>
		</DialogTrigger>
	);
}

/** The controls above the table: colleague search, the By bundle / By colleague view and Filters. */
export function InventoryToolbar(props: FilterProps): ReactNode {
	const classes = useStyles();
	const { filters, onChange } = props;
	return (
		<div className={classes.controls}>
			<SearchField
				aria-label="Colleague"
				placeholder="Search colleague, email or slug"
				size="small"
				value={filters.q ?? ""}
				onChange={(q) => onChange({ ...filters, q: q || undefined })}
			/>
			<ToggleButtonGroup
				aria-label="Group rows"
				selectionMode="single"
				disallowEmptySelection
				selectedKeys={[filters.group ?? "bundle"]}
				onSelectionChange={(keys) =>
					onChange({ ...filters, group: keys.has("colleague") ? "colleague" : undefined })
				}
			>
				<ToggleButton id="bundle">By bundle</ToggleButton>
				<ToggleButton id="colleague">By colleague</ToggleButton>
			</ToggleButtonGroup>
			<FiltersButton {...props} />
		</div>
	);
}

/** What is narrowing the table, one removable tag per selected value, with Clear all. */
export function ActiveFilterTags(props: FilterProps): ReactNode {
	const classes = useStyles();
	const { items, types, filters, descriptors, onChange } = props;
	if (activeFilterCount(filters) === 0) return null;
	const options = groupOptions(items, types, { ...filters, type: undefined }, descriptors);
	const tags = FILTER_GROUPS.flatMap((group) =>
		(filters[group] ?? []).map((value) => ({
			id: `${group}:${value}`,
			group,
			value,
			label: `${GROUP_LABEL[group]}: ${options[group].find((o) => o.value === value)?.label ?? value}`,
		})),
	);
	return (
		<div className={classes.tags}>
			<TagGroup
				aria-label="Active filters"
				items={tags}
				onRemove={(keys) => {
					let next = filters;
					for (const tag of tags.filter((t) => keys.has(t.id))) {
						const left = (next[tag.group] ?? []).filter((v) => v !== tag.value);
						next = withGroup(next, tag.group, left, items, descriptors);
					}
					onChange(next);
				}}
			>
				{(tag) => <Tag id={tag.id}>{tag.label}</Tag>}
			</TagGroup>
			<Button
				variant="tertiary"
				size="small"
				onPress={() => onChange(clearPopoverFilters(filters))}
			>
				Clear all
			</Button>
		</div>
	);
}
